import { Button, theme } from "antd";
import { Paperclip, Send } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

interface ChatInputProps {
	// Resolves false when the send was dropped (busy/offline) so the input can
	// restore the typed text instead of losing it silently.
	onSend: (message: string) => void | boolean | Promise<void | boolean>;
	isLoading: boolean;
	disabled?: boolean;
	autoFocus?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
	onSend,
	isLoading,
	disabled,
	autoFocus,
}) => {
	const { token } = theme.useToken();
	const [value, setValue] = useState("");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const adjustHeight = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
		}
	};

	useEffect(() => {
		adjustHeight();
	}, [value]);

	useEffect(() => {
		if (autoFocus && textareaRef.current) {
			setTimeout(() => {
				textareaRef.current?.focus();
			}, 100);
		}
	}, [autoFocus]);

	const handleSend = () => {
		if (value.trim() && !isLoading) {
			const text = value;
			// Clear optimistically for a snappy input, but restore the text if the
			// send was dropped (onSend resolves false) — the accept boolean can lag
			// (a real send resolves only when the SSE stream closes), so waiting to
			// clear would freeze the input for the whole turn.
			setValue("");
			if (textareaRef.current) {
				textareaRef.current.style.height = "auto";
				textareaRef.current.focus();
			}
			void Promise.resolve(onSend(text)).then((accepted) => {
				if (accepted === false) {
					setValue(text);
				}
			});
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	return (
		<div
			style={{
				padding: 14,
				background: token.colorBgContainer,
				borderTop: `1px solid ${token.colorBorderSecondary}`,
				borderRadius: "0 0 16px 16px",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "flex-end",
					gap: 8,
					background: token.colorBgLayout,
					padding: "6px 6px 6px 8px",
					borderRadius: 22,
					border: `1px solid ${token.colorBorder}`,
				}}
			>
				<Button
					type="text"
					shape="circle"
					icon={<Paperclip size={18} />}
					style={{ color: token.colorTextSecondary }}
					disabled={disabled}
				/>

				<textarea
					ref={textareaRef}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder="Ask anything..."
					disabled={disabled}
					style={{
						flex: 1,
						border: "none",
						background: "transparent",
						resize: "none",
						outline: "none",
						padding: "6px 0",
						maxHeight: 120,
						minHeight: 24,
						color: token.colorText,
						fontSize: 14,
						lineHeight: 1.5,
						fontFamily: "inherit",
					}}
					rows={1}
				/>

				<Button
					type="primary"
					shape="circle"
					icon={<Send size={16} />}
					onClick={handleSend}
					loading={isLoading}
					disabled={!value.trim() || disabled}
					style={{
						boxShadow: token.boxShadow,
					}}
				/>
			</div>
		</div>
	);
};
